import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import SearchBar from '../components/SearchBar';
import Pagination from '../components/Common/Pagination';
import { fetchPackagesPage, deletePackage } from '../api/Packages';
import { TravelPackage } from '../types/TravelPackage';
import { calculatePackageStartingPrice, normalizePackageCabPricing } from '../utils/packageTemplate';
import '../css/PackagesPage.css';
import * as FiIcons from 'react-icons/fi';

const FiPlus = FiIcons.FiPlus as unknown as React.FC;
const FiCopy = FiIcons.FiCopy as unknown as React.FC;
const FiEye = FiIcons.FiEye as unknown as React.FC;
const FiEdit2 = FiIcons.FiEdit2 as unknown as React.FC;
const FiTrash2 = FiIcons.FiTrash2 as unknown as React.FC;

type SortKey = 'title' | 'slug' | 'duration' | 'cities' | 'price' | 'published' | 'updated';
const PAGE_SIZE = 20;

const getPackageListPrice = (pkg: TravelPackage) => {
  const explicit = Number(pkg.publicStartingPrice ?? pkg.public_starting_price ?? 0);
  if (explicit > 0) return explicit;
  const base = Number(pkg.baseStartingPrice ?? pkg.base_starting_price ?? 0);
  if (base > 0) return calculatePackageStartingPrice(base, normalizePackageCabPricing(pkg));
  return Number(pkg.price?.base || 0);
};

const PackagesPage: React.FC = () => {
  const navigate = useNavigate();
  const listRef = useRef<HTMLDivElement | null>(null);

  const [packages, setPackages] = useState<TravelPackage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [onlyPublished, setOnlyPublished] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('updated');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const load = useCallback(async (nextPage: number, searchTerm: string) => {
    try {
      setLoading(true);
      setError('');
      const result = await fetchPackagesPage({
        search: searchTerm.trim() || undefined,
        page: nextPage,
        pageSize: PAGE_SIZE,
      });
      setPackages(Array.isArray(result.items) ? result.items : []);
      setTotalPages(result.pagination.totalPages || 1);
      setPage(result.pagination.page || nextPage);
    } catch (e: any) {
      setError(e?.message || 'Failed to load packages');
      setPackages([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, []);

  const prevQueryRef = useRef({ search: '', city: '' });
  useEffect(() => {
    const searchTerm = search.trim();
    const prev = prevQueryRef.current;
    const queryChanged = prev.search !== searchTerm;

    if (queryChanged && page !== 1) {
      setPage(1);
      prevQueryRef.current = { search: searchTerm, city: '' };
      return;
    }

    prevQueryRef.current = { search: searchTerm, city: '' };
    load(page, searchTerm);
  }, [load, page, search]);

  useEffect(() => {
    setPage(1);
  }, [sortKey, sortDir, onlyPublished]);

  const filtered = useMemo(() => {
    const base = packages.filter(p => (!onlyPublished || !!p.is_published));

    const withDerived = base.map(p => ({
      ...p,
      __duration: `${p.duration_days || 0}d/${p.duration_nights || 0}n`,
      __cities: (p.cities || []).join(', '),
      __price: getPackageListPrice(p),
      __updated: p.updated_at || p.created_at || '',
    }));

    const dir = sortDir === 'asc' ? 1 : -1;
    return withDerived.slice().sort((a, b) => {
      switch (sortKey) {
        case 'title':
          return a.title.localeCompare(b.title) * dir;
        case 'slug':
          return (a.slug || '').localeCompare(b.slug || '') * dir;
        case 'duration': {
          const an = a.duration_nights || 0; const bn = b.duration_nights || 0;
          if (an === bn) return ((a.duration_days || 0) - (b.duration_days || 0)) * dir;
          return (an - bn) * dir;
        }
        case 'cities':
          return a.__cities.localeCompare(b.__cities) * dir;
        case 'price':
          return ((a.__price || 0) - (b.__price || 0)) * dir;
        case 'published':
          return ((a.is_published ? 1 : 0) - (b.is_published ? 1 : 0)) * dir;
        case 'updated':
        default: {
          const at = a.__updated ? new Date(a.__updated).getTime() : 0;
          const bt = b.__updated ? new Date(b.__updated).getTime() : 0;
          return (at - bt) * dir;
        }
      }
    });
  }, [packages, sortKey, sortDir, onlyPublished]);

  const pagedPackages = filtered;

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const onModifyCopy = (p: TravelPackage) => {
    const copy = { ...p };
    navigate('/package/builder', { state: { packageToCopy: copy } });
  };

  const onView = (p: TravelPackage) => {
    if (!p.slug) return;
    const url = `https://otaprime.com/packages/${encodeURIComponent(p.slug)}`;
    window.open(url, '_blank', 'noopener');
  };

  const onEdit = (p: TravelPackage) => {
    navigate('/package/builder', { state: { packageToEdit: p } });
  };

  const onDelete = async (p: TravelPackage) => {
    if (!p.id) return;
    const ok = window.confirm(`Delete package "${p.title}"?`);
    if (!ok) return;
    try {
      await deletePackage(String(p.id));
      setPackages(prev => prev.filter(x => x.id !== p.id));
    } catch (e: any) {
      alert(e?.message || 'Failed to delete package');
    }
  };

  return (
    <Layout>
      <div className="packages-page">
        <div className="packages-header">
          <h2>Packages</h2>
          <div className="packages-actions">
            <button className="btn" onClick={() => navigate('/package/builder')}>
              <FiPlus /> New Package
            </button>
          </div>
        </div>

        <div className="packages-filters">
          <SearchBar searchTerm={search} onSearchChange={setSearch} placeholder="Search by title, slug, city" />
          <label className="checkbox">
            <input type="checkbox" checked={onlyPublished} onChange={e => setOnlyPublished(e.target.checked)} />
            <span>Only published</span>
          </label>
        </div>

        {error && <div className="packages-error">{error}</div>}

        <div className="packages-table-wrapper" ref={listRef}>
          <table className="packages-table">
            <thead>
              <tr>
                <th onClick={() => toggleSort('title')}>
                  Title {sortKey === 'title' && (sortDir === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => toggleSort('duration')}>
                  Duration {sortKey === 'duration' && (sortDir === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => toggleSort('cities')}>
                  Cities {sortKey === 'cities' && (sortDir === 'asc' ? '▲' : '▼')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedPackages.map(p => (
                <tr key={p.id || p.slug}>
                  <td className="col-title">{p.title}</td>
                  <td className="col-duration">{(p.duration_days || 0)}d/{(p.duration_nights || 0)}n</td>
                  <td className="col-cities">{(p.cities || []).join(', ')}</td>
                  <td className="col-actions">
                    <div className="action-buttons">
                      {(() => { const canView = !!p.is_published && !!p.slug; return (
                        <button className="btn-secondary icon-only" title={canView ? 'View' : 'View (published only)'} onClick={() => canView && onView(p)} disabled={!canView}>
                          <FiEye />
                        </button>
                      ); })()}
                      <button className="btn-secondary icon-only" title="Edit" onClick={() => onEdit(p)}>
                        <FiEdit2 />
                      </button>
                      <button className="btn-secondary icon-only" title="Copy" onClick={() => onModifyCopy(p)}>
                        <FiCopy />
                      </button>
                      <button className="btn-secondary icon-only danger" title="Delete" onClick={() => onDelete(p)}>
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && !loading && (
                <tr>
                  <td colSpan={8} className="empty-state">No packages found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
      </div>
    </Layout>
  );
};

export default PackagesPage;
